import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_OTHER_ACTION, CreateDialogModalFrame } from 'components/create-dialog/CreateDialogModalFrame';
import { CreateDialogSection } from 'components/create-dialog/CreateDialogSection';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { InvoiceData, InvoiceRowForm, InvoiceRowTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';
import { FEE_AMOUNT_FIELD, FEE_DETAILS_SECTION, feeDialogRecord } from '../feeDialogRecord';

interface Props {
    action: 'create' | 'update';
    initialData: InvoiceRowForm;
    onClose: () => void;
    onSubmit: (data: InvoiceRowForm) => void;
    onDelete?: (invoiceRowId: number) => void;
    invoiceRowTotals?: InvoiceRowTotals;
    invoice: InvoiceData;
    baseAmount: number;
    autoSyncDescription?: boolean;
    adminFeeLabel?: string;
}

export const AdminFeeEditModal = (props: Props) => {
    const {
        action,
        onClose,
        onSubmit,
        onDelete,
        invoiceRowTotals,
        initialData,
        invoice,
        baseAmount,
        autoSyncDescription,
        adminFeeLabel,
    } = props;
    const isInViewMode = !invoice.isEditable;
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    // A shared-payment copy's amounts are the group's: its admin fee keeps its rate and only rewords.
    const { isSharedPaymentCopy: isDescriptionOnly } = useSharedPayment(invoice);

    const {
        handleSubmit,
        control,
        setValue,
        formState: { dirtyFields },
    } = useForm<InvoiceRowForm>({
        defaultValues: initialData,
        values: initialData,
        mode: 'onChange',
    });

    const handleOnSubmit = (data: InvoiceRowForm) => {
        onSubmit(data);
        onClose();
    };

    const onAdminFeeRateChange = useCallback(
        (value: string | undefined) => {
            const rate = value ? Number(value) : NaN;
            const isValid = !isNaN(rate);

            setValue('itemTotal', isValid ? Math.round(((baseAmount * rate) / 100) * 100) / 100 : 0);

            if (autoSyncDescription && !dirtyFields.description && adminFeeLabel) {
                const next = isValid && rate ? `${adminFeeLabel} ${rate.toFixed(2)}%` : adminFeeLabel;
                setValue('description', next, { shouldDirty: false });
            }
        },
        [baseAmount, setValue, autoSyncDescription, adminFeeLabel, dirtyFields.description]
    );

    const isDeletable = !!onDelete && !!invoiceRowTotals && !isInViewMode && !isDescriptionOnly;
    const handleDelete = () => {
        if (onDelete && invoiceRowTotals) {
            onDelete(invoiceRowTotals.invoiceRowId);
            onClose();
        }
    };
    const submit = () => {
        void handleSubmit(handleOnSubmit)();
    };

    const isNewDesign = useIsNewDesign();
    // The new design's Details: the description on its own row, then the base amount and the rate; amounts at the end.
    const amountProps = isNewDesign ? FEE_AMOUNT_FIELD : {};
    const descriptionField = (
        <TextInput
            control={control}
            name="description"
            label="invoices.totals-tab.description"
            wrapperClassName={isNewDesign ? 'col-span-full' : undefined}
        />
    );
    const baseAmountField = (
        <TextInput
            control={control}
            label="invoices.totals-tab.base-amount"
            name="amountBeforeDiscount"
            disabled
            {...amountProps}
        />
    );
    const rateField = (
        <PercentageInput
            control={control}
            name="adminFeeRate"
            decimalPlaces={2}
            maxWholeDigits={3}
            label="invoices.totals-tab.admin-fee-rate"
            {...amountProps}
            onChange={(event) => onAdminFeeRateChange(event.target.value)}
            validate={(value) => {
                if (value != null && Number(value) < 0) {
                    return t('validation.must-be-positive');
                }
                return true;
            }}
        />
    );
    const totalField = (
        <TextInput
            control={control}
            label="invoices.totals-tab.total-wo-vat"
            name="itemTotal"
            disabled
            required
            {...amountProps}
        />
    );

    if (isNewDesign) {
        return (
            <CreateDialogModalFrame
                {...feeDialogRecord(invoice, initialData.matterId)}
                title={t(`invoices.totals-tab.${action}-admin-fee`)}
                notice={isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
                openSection={FEE_DETAILS_SECTION}
                onCancel={onClose}
                otherActions={
                    isDeletable ? (
                        <Button {...CREATE_DIALOG_OTHER_ACTION} onClick={handleDelete}>
                            {t('delete')}
                        </Button>
                    ) : null
                }
                mainAction={
                    isInViewMode
                        ? undefined
                        : { label: t(action === 'create' ? 'invoices.totals-tab.create' : 'save'), onClick: submit }
                }
            >
                <CreateDialogSection title={FEE_DETAILS_SECTION}>
                    {isDescriptionOnly ? null : totalField}
                    {descriptionField}
                    {isDescriptionOnly ? null : (
                        <>
                            {baseAmountField}
                            {rateField}
                        </>
                    )}
                </CreateDialogSection>
            </CreateDialogModalFrame>
        );
    }

    const actions = [
        <Button key="cancel" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onClose}>
            {t('cancel')}
        </Button>,
    ];

    if (!isInViewMode) {
        actions.push(
            <Button key="update-fee" {...buttonStyle({ variant: 'contained', color: 'secondary' })} onClick={submit}>
                {t(`invoices.totals-tab.${action}`)}
            </Button>
        );
    }

    if (isDeletable) {
        actions.push(
            <Button key="delete" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={handleDelete}>
                {t('delete')}
            </Button>
        );
    }

    return (
        <>
            <ModalHeader title={t(`invoices.totals-tab.${action}-admin-fee`)} onCancel={onClose} />
            <div className="h-full overflow-y-auto p-6 pb-8 max-sm:px-4">
                {isPaymentCurrencyMode && <PaymentCurrencyNotice currency={displayCurrency} className="mb-4" />}
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12">{descriptionField}</div>
                    {isDescriptionOnly ? null : (
                        <>
                            <div className={cn('col-span-4', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {baseAmountField}
                            </div>
                            <div className={cn('col-span-4', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {rateField}
                            </div>
                            <div className={cn('col-span-4', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                {totalField}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ModalFooterSimple buttons={actions} />
        </>
    );
};
